import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getTrainings,
  getTrainingById,
  getTrainingsByDateRange,
  getTrainingsByLocation,
  getTrainingsByInstructor,
  getTrainingsByTechnique,
  searchTrainings,
  addTraining,
  updateTraining,
  deleteTraining,
  addTrainingPhoto,
  deleteTrainingPhoto,
  addTrainingVideo,
  deleteTrainingVideo,
  updateTrainingVisibility,
  getTrainingStats,
  getMostUsedTechniques,
  Training,
  TrainingInput,
  QueryOptions,
  TrainingStats
} from '../../services/firebase/training.service';

// Types
export interface PendingChange {
  id: string;
  type: 'create' | 'update' | 'delete';
  entityId: string;
  data: any;
  timestamp: number;
  status: 'pending' | 'syncing' | 'completed' | 'failed';
}

interface TrainingState {
  trainings: Record<string, Training>;
  trainingIds: string[];
  totalResults: number;
  currentTraining: Training | null;
  isLoading: boolean;
  error: string | null;
  stats: TrainingStats | null;
  mostUsedTechniques: Array<{
    id: string;
    name: string;
    count: number;
    type: string;
  }> | null;
  lastQueryOptions: QueryOptions | null;
  // Offline support
  isOnline: boolean;
  pendingChanges: PendingChange[];
  lastSyncedAt: number | null;
  // Error handling
  permissionError: { message: string; timestamp: number } | null;
}

// Initial state
const initialState: TrainingState = {
  trainings: {},
  trainingIds: [],
  totalResults: 0,
  currentTraining: null,
  isLoading: false,
  error: null,
  stats: null,
  mostUsedTechniques: null,
  lastQueryOptions: null,
  permissionError: null,
  // Offline support
  isOnline: navigator.onLine,
  pendingChanges: [],
  lastSyncedAt: null
};

// Async thunks
export const fetchUserTrainings = createAsyncThunk<
  { trainings: Training[]; options: QueryOptions | null },
  { uid: string; options?: QueryOptions },
  { rejectValue: string }
>('training/fetchUserTrainings', async ({ uid, options }, { rejectWithValue }) => {
  try {
    const trainings = await getTrainings(uid, options);
    return { trainings, options: options || null };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch trainings');
  }
});

export const fetchTrainingById = createAsyncThunk<
  Training,
  string,
  { rejectValue: string }
>('training/fetchTrainingById', async (trainingId, { rejectWithValue }) => {
  try {
    const training = await getTrainingById(trainingId);
    if (!training) {
      return rejectWithValue('Training not found');
    }
    return training;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch training');
  }
});

export const fetchTrainingsByDateRange = createAsyncThunk<
  Training[],
  { uid: string; startDate: number; endDate: number },
  { rejectValue: string }
>('training/fetchTrainingsByDateRange', async ({ uid, startDate, endDate }, { rejectWithValue }) => {
  try {
    const trainings = await getTrainingsByDateRange(uid, startDate, endDate);
    return trainings;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch trainings by date range');
  }
});

export const fetchTrainingsByLocation = createAsyncThunk<
  Training[],
  { uid: string; locationId: string },
  { rejectValue: string }
>('training/fetchTrainingsByLocation', async ({ uid, locationId }, { rejectWithValue }) => {
  try {
    const trainings = await getTrainingsByLocation(uid, locationId);
    return trainings;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch trainings by location');
  }
});

export const fetchTrainingsByInstructor = createAsyncThunk<
  Training[],
  { uid: string; instructorId: string },
  { rejectValue: string }
>('training/fetchTrainingsByInstructor', async ({ uid, instructorId }, { rejectWithValue }) => {
  try {
    const trainings = await getTrainingsByInstructor(uid, instructorId);
    return trainings;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch trainings by instructor');
  }
});

export const fetchTrainingsByTechnique = createAsyncThunk<
  Training[],
  { uid: string; techniqueId: string },
  { rejectValue: string }
>('training/fetchTrainingsByTechnique', async ({ uid, techniqueId }, { rejectWithValue }) => {
  try {
    const trainings = await getTrainingsByTechnique(uid, techniqueId);
    return trainings;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch trainings by technique');
  }
});

export const searchUserTrainings = createAsyncThunk<
  Training[],
  { uid: string; searchText: string },
  { rejectValue: string }
>('training/searchUserTrainings', async ({ uid, searchText }, { rejectWithValue }) => {
  try {
    const trainings = await searchTrainings(uid, searchText);
    return trainings;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to search trainings');
  }
});

export const addNewTraining = createAsyncThunk<
  Training,
  TrainingInput,
  { rejectValue: string }
>('training/addNewTraining', async (data, { rejectWithValue }) => {
  try {
    const training = await addTraining(data);
    return training;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to add training');
  }
});

export const updateExistingTraining = createAsyncThunk<
  Training,
  { id: string; data: Partial<Training> },
  { rejectValue: string }
>('training/updateExistingTraining', async ({ id, data }, { rejectWithValue }) => {
  try {
    const training = await updateTraining(id, data);
    return training;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update training');
  }
});

export const deleteExistingTraining = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('training/deleteExistingTraining', async (id, { rejectWithValue }) => {
  try {
    await deleteTraining(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete training');
  }
});

export const addPhotoToTraining = createAsyncThunk<
  { trainingId: string; photoId: string },
  { trainingId: string; file: File; caption?: string; aspectRatio?: number },
  { rejectValue: string }
>('training/addPhotoToTraining', async ({ trainingId, file, caption, aspectRatio }, { rejectWithValue }) => {
  try {
    const photoId = await addTrainingPhoto(trainingId, file, caption, aspectRatio);
    return { trainingId, photoId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to add photo to training');
  }
});

export const deletePhotoFromTraining = createAsyncThunk<
  { trainingId: string; photoId: string },
  { trainingId: string; photoId: string },
  { rejectValue: string }
>('training/deletePhotoFromTraining', async ({ trainingId, photoId }, { rejectWithValue }) => {
  try {
    await deleteTrainingPhoto(trainingId, photoId);
    return { trainingId, photoId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete photo from training');
  }
});

export const addVideoToTraining = createAsyncThunk<
  { trainingId: string; videoId: string },
  { trainingId: string; youtubeId: string; title?: string },
  { rejectValue: string }
>('training/addVideoToTraining', async ({ trainingId, youtubeId, title }, { rejectWithValue }) => {
  try {
    const videoId = await addTrainingVideo(trainingId, youtubeId, title);
    return { trainingId, videoId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to add video to training');
  }
});

export const deleteVideoFromTraining = createAsyncThunk<
  { trainingId: string; videoId: string },
  { trainingId: string; videoId: string },
  { rejectValue: string }
>('training/deleteVideoFromTraining', async ({ trainingId, videoId }, { rejectWithValue }) => {
  try {
    await deleteTrainingVideo(trainingId, videoId);
    return { trainingId, videoId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete video from training');
  }
});

export const updateTrainingVisibilitySettings = createAsyncThunk<
  { trainingId: string; isPublic?: boolean; shownOnFeed?: boolean },
  { trainingId: string; isPublic?: boolean; shownOnFeed?: boolean },
  { rejectValue: string }
>('training/updateTrainingVisibilitySettings', async ({ trainingId, isPublic, shownOnFeed }, { rejectWithValue }) => {
  try {
    await updateTrainingVisibility(trainingId, isPublic, shownOnFeed);
    return { trainingId, isPublic, shownOnFeed };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update training visibility');
  }
});

// Like functionality removed - use the unified likes.service.ts instead

export const fetchTrainingStats = createAsyncThunk<
  TrainingStats,
  string,
  { rejectValue: string }
>('training/fetchTrainingStats', async (uid, { rejectWithValue }) => {
  try {
    const stats = await getTrainingStats(uid);
    return stats;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch training stats');
  }
});

export const fetchMostUsedTechniques = createAsyncThunk<
  Array<{ id: string; name: string; count: number; type: string }>,
  { uid: string; limit?: number },
  { rejectValue: string }
>('training/fetchMostUsedTechniques', async ({ uid, limit }, { rejectWithValue }) => {
  try {
    const techniques = await getMostUsedTechniques(uid, limit);
    return techniques;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch most used techniques');
  }
});

// Slice
const trainingSlice = createSlice({
  name: 'training',
  initialState,
  reducers: {
    clearCurrentTraining: (state) => {
      state.currentTraining = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearPermissionError: (state) => {
      state.permissionError = null;
    },
    clearTrainings: (state) => {
      state.trainings = {};
      state.trainingIds = [];
      state.totalResults = 0;
    },
    // Handle permission errors from Firebase
    permissionError: (state, action: PayloadAction<{ message: string; timestamp: number }>) => {
      state.permissionError = action.payload;
      // Don't set the general error since this is a specific type of error
      // that may need special UI handling
    },
    // Actions for realtime updates from Firebase
    updateTrainingsFromFirebase: (state, action: PayloadAction<Training[]>) => {
      // Store trainings in map by ID
      const trainingsMap: Record<string, Training> = {};
      const trainingIds: string[] = [];
      
      action.payload.forEach(training => {
        trainingsMap[training.id] = training;
        trainingIds.push(training.id);
      });
      
      state.trainings = trainingsMap;
      state.trainingIds = trainingIds;
      state.totalResults = trainingIds.length;
      state.lastSyncedAt = Date.now();
    },
    addTraining: (state, action: PayloadAction<Training>) => {
      const training = action.payload;
      state.trainings[training.id] = training;
      
      // Add to beginning of IDs array
      if (!state.trainingIds.includes(training.id)) {
        state.trainingIds.unshift(training.id);
        state.totalResults++;
      }
    },
    updateTraining: (state, action: PayloadAction<{ id: string; data: Training }>) => {
      const { id, data } = action.payload;
      state.trainings[id] = data;
      
      // If this is the current training, update it
      if (state.currentTraining && state.currentTraining.id === id) {
        state.currentTraining = data;
      }
    },
    removeTraining: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.trainings[id];
      state.trainingIds = state.trainingIds.filter(trainingId => trainingId !== id);
      state.totalResults--;
      
      // If this is the current training, clear it
      if (state.currentTraining && state.currentTraining.id === id) {
        state.currentTraining = null;
      }
    },
    // Offline support
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    addPendingChange: (state, action: PayloadAction<Omit<PendingChange, 'id' | 'timestamp' | 'status'>>) => {
      const pendingChange: PendingChange = {
        id: Date.now().toString(36) + Math.random().toString(36).substring(2),
        timestamp: Date.now(),
        status: 'pending',
        ...action.payload
      };
      
      state.pendingChanges.push(pendingChange);
    },
    updatePendingChangeStatus: (state, action: PayloadAction<{ id: string; status: PendingChange['status'] }>) => {
      const { id, status } = action.payload;
      const pendingChange = state.pendingChanges.find(change => change.id === id);
      
      if (pendingChange) {
        pendingChange.status = status;
      }
    },
    removePendingChange: (state, action: PayloadAction<string>) => {
      state.pendingChanges = state.pendingChanges.filter(change => change.id !== action.payload);
    },
    clearPendingChanges: (state) => {
      state.pendingChanges = [];
    },
    syncComplete: (state) => {
      state.lastSyncedAt = Date.now();
      // Clear all completed and failed pending changes to prevent unbounded growth
      state.pendingChanges = state.pendingChanges.filter(change =>
        change.status !== 'completed' && change.status !== 'failed'
      );
    }
  },
  extraReducers: (builder) => {
    // Fetch user trainings
    builder.addCase(fetchUserTrainings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchUserTrainings.fulfilled, (state, action) => {
      state.isLoading = false;
      // Store trainings in map by ID
      const trainingsMap: Record<string, Training> = {};
      const trainingIds: string[] = [];
      
      action.payload.trainings.forEach(training => {
        trainingsMap[training.id] = training;
        trainingIds.push(training.id);
      });
      
      state.trainings = trainingsMap;
      state.trainingIds = trainingIds;
      state.totalResults = trainingIds.length;
      state.lastQueryOptions = action.payload.options;
    });
    builder.addCase(fetchUserTrainings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch trainings';
    });
    
    // Fetch training by id
    builder.addCase(fetchTrainingById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTrainingById.fulfilled, (state, action) => {
      state.isLoading = false;
      // Set current training
      state.currentTraining = action.payload;
      // Also add to map
      state.trainings[action.payload.id] = action.payload;
      
      // Add to IDs list if not already there
      if (!state.trainingIds.includes(action.payload.id)) {
        state.trainingIds.push(action.payload.id);
      }
    });
    builder.addCase(fetchTrainingById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch training';
    });
    
    // Fetch trainings by date range
    builder.addCase(fetchTrainingsByDateRange.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTrainingsByDateRange.fulfilled, (state, action) => {
      state.isLoading = false;
      // Store trainings in map by ID
      const trainingsMap: Record<string, Training> = {};
      const trainingIds: string[] = [];
      
      action.payload.forEach(training => {
        trainingsMap[training.id] = training;
        trainingIds.push(training.id);
      });
      
      state.trainings = trainingsMap;
      state.trainingIds = trainingIds;
      state.totalResults = trainingIds.length;
    });
    builder.addCase(fetchTrainingsByDateRange.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch trainings by date range';
    });
    
    // Fetch trainings by location
    builder.addCase(fetchTrainingsByLocation.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTrainingsByLocation.fulfilled, (state, action) => {
      state.isLoading = false;
      // Store trainings in map by ID
      const trainingsMap: Record<string, Training> = {};
      const trainingIds: string[] = [];
      
      action.payload.forEach(training => {
        trainingsMap[training.id] = training;
        trainingIds.push(training.id);
      });
      
      state.trainings = trainingsMap;
      state.trainingIds = trainingIds;
      state.totalResults = trainingIds.length;
    });
    builder.addCase(fetchTrainingsByLocation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch trainings by location';
    });
    
    // Fetch trainings by instructor
    builder.addCase(fetchTrainingsByInstructor.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTrainingsByInstructor.fulfilled, (state, action) => {
      state.isLoading = false;
      // Store trainings in map by ID
      const trainingsMap: Record<string, Training> = {};
      const trainingIds: string[] = [];
      
      action.payload.forEach(training => {
        trainingsMap[training.id] = training;
        trainingIds.push(training.id);
      });
      
      state.trainings = trainingsMap;
      state.trainingIds = trainingIds;
      state.totalResults = trainingIds.length;
    });
    builder.addCase(fetchTrainingsByInstructor.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch trainings by instructor';
    });
    
    // Fetch trainings by technique
    builder.addCase(fetchTrainingsByTechnique.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTrainingsByTechnique.fulfilled, (state, action) => {
      state.isLoading = false;
      // Store trainings in map by ID
      const trainingsMap: Record<string, Training> = {};
      const trainingIds: string[] = [];
      
      action.payload.forEach(training => {
        trainingsMap[training.id] = training;
        trainingIds.push(training.id);
      });
      
      state.trainings = trainingsMap;
      state.trainingIds = trainingIds;
      state.totalResults = trainingIds.length;
    });
    builder.addCase(fetchTrainingsByTechnique.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch trainings by technique';
    });
    
    // Search trainings
    builder.addCase(searchUserTrainings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(searchUserTrainings.fulfilled, (state, action) => {
      state.isLoading = false;
      // Store trainings in map by ID
      const trainingsMap: Record<string, Training> = {};
      const trainingIds: string[] = [];
      
      action.payload.forEach(training => {
        trainingsMap[training.id] = training;
        trainingIds.push(training.id);
      });
      
      state.trainings = trainingsMap;
      state.trainingIds = trainingIds;
      state.totalResults = trainingIds.length;
    });
    builder.addCase(searchUserTrainings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to search trainings';
    });
    
    // Add new training
    builder.addCase(addNewTraining.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addNewTraining.fulfilled, (state, action) => {
      state.isLoading = false;
      // Add new training to state
      state.trainings[action.payload.id] = action.payload;
      state.trainingIds.unshift(action.payload.id); // Add to beginning
      state.totalResults++;
      state.currentTraining = action.payload;
    });
    builder.addCase(addNewTraining.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to add training';
    });
    
    // Update training
    builder.addCase(updateExistingTraining.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateExistingTraining.fulfilled, (state, action) => {
      state.isLoading = false;
      // Update training in state
      state.trainings[action.payload.id] = action.payload;
      
      // If this is the current training, update it
      if (state.currentTraining && state.currentTraining.id === action.payload.id) {
        state.currentTraining = action.payload;
      }
    });
    builder.addCase(updateExistingTraining.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update training';
    });
    
    // Delete training
    builder.addCase(deleteExistingTraining.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteExistingTraining.fulfilled, (state, action) => {
      state.isLoading = false;
      // Remove training from state
      const id = action.payload;
      delete state.trainings[id];
      state.trainingIds = state.trainingIds.filter(trainingId => trainingId !== id);
      state.totalResults--;
      
      // If this is the current training, clear it
      if (state.currentTraining && state.currentTraining.id === id) {
        state.currentTraining = null;
      }
    });
    builder.addCase(deleteExistingTraining.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to delete training';
    });
    
    // Add photo to training
    builder.addCase(addPhotoToTraining.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addPhotoToTraining.fulfilled, (state, action) => {
      state.isLoading = false;
      // We need to refetch the training to get the updated photo data
      // Will be done by component after this action
    });
    builder.addCase(addPhotoToTraining.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to add photo to training';
    });
    
    // Delete photo from training
    builder.addCase(deletePhotoFromTraining.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deletePhotoFromTraining.fulfilled, (state, action) => {
      state.isLoading = false;
      // We need to refetch the training to get the updated photos data
      // Will be done by component after this action
    });
    builder.addCase(deletePhotoFromTraining.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to delete photo from training';
    });
    
    // Add video to training
    builder.addCase(addVideoToTraining.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addVideoToTraining.fulfilled, (state, action) => {
      state.isLoading = false;
      // We need to refetch the training to get the updated video data
      // Will be done by component after this action
    });
    builder.addCase(addVideoToTraining.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to add video to training';
    });
    
    // Delete video from training
    builder.addCase(deleteVideoFromTraining.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteVideoFromTraining.fulfilled, (state, action) => {
      state.isLoading = false;
      // We need to refetch the training to get the updated videos data
      // Will be done by component after this action
    });
    builder.addCase(deleteVideoFromTraining.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to delete video from training';
    });
    
    // Update training visibility
    builder.addCase(updateTrainingVisibilitySettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateTrainingVisibilitySettings.fulfilled, (state, action) => {
      state.isLoading = false;
      const { trainingId, isPublic, shownOnFeed } = action.payload;
      
      // Update values in state if we have the training
      if (state.trainings[trainingId]) {
        if (isPublic !== undefined) {
          state.trainings[trainingId].isPublic = isPublic;
        }
        if (shownOnFeed !== undefined) {
          state.trainings[trainingId].shownOnFeed = shownOnFeed; // Use correct field name
          (state.trainings[trainingId] as any).shownOnFeeds = shownOnFeed;
        }
      }
      
      // Update current training if this is it
      if (state.currentTraining && state.currentTraining.id === trainingId) {
        if (isPublic !== undefined) {
          state.currentTraining.isPublic = isPublic;
        }
        if (shownOnFeed !== undefined) {
          state.currentTraining.shownOnFeed = shownOnFeed; // Use correct field name
          (state.currentTraining as any).shownOnFeeds = shownOnFeed;
        }
      }
    });
    builder.addCase(updateTrainingVisibilitySettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update training visibility';
    });
    
    // Like functionality removed - use the unified likes.service.ts instead
    
    // Fetch training stats
    builder.addCase(fetchTrainingStats.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTrainingStats.fulfilled, (state, action) => {
      state.isLoading = false;
      state.stats = action.payload;
    });
    builder.addCase(fetchTrainingStats.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch training stats';
    });
    
    // Fetch most used techniques
    builder.addCase(fetchMostUsedTechniques.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchMostUsedTechniques.fulfilled, (state, action) => {
      state.isLoading = false;
      state.mostUsedTechniques = action.payload;
    });
    builder.addCase(fetchMostUsedTechniques.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch most used techniques';
    });
  }
});

export const {
  clearCurrentTraining,
  clearError,
  clearPermissionError,
  clearTrainings,
  updateTrainingsFromFirebase,
  addTraining: addTrainingAction,
  updateTraining: updateTrainingAction,
  removeTraining,
  permissionError,
  // Offline support actions
  setOnlineStatus,
  addPendingChange,
  updatePendingChangeStatus,
  removePendingChange,
  clearPendingChanges,
  syncComplete
} = trainingSlice.actions;

export default trainingSlice.reducer;
